import React, { useMemo } from 'react';
import { SparkJob } from '../types/sparkTypes';
import {
  analyzeParallelProgress,
  formatEta,
  formatParallelStatus,
  getJobDisplayStatus,
  useAnimatedProgress,
} from '../hooks/useAnimatedProgress';
import { JobProgressBar } from './shared/JobProgressBar';
import { JobResultNavigation } from '../utils/jobNavigation';
import styles from '../styles/ActiveJobCard.module.css';

interface ActiveJobCardProps {
  job: SparkJob;
  onClick?: (job: SparkJob) => void;
  completionTitle?: string;
  completionSummary?: string;
  resultNavigation?: JobResultNavigation | null;
  onDismissCompletion?: (job: SparkJob) => void;
}

/**
 * Formats a timestamp as a relative time string (e.g., "2m ago", "1h ago")
 */
function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 60) {
    return 'just now';
  }
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}


/**
 * ActiveJobCard displays a running or pending Spark job in the CreativeBoard.
 * Shows job name, status, latest status message, and optional progress bar.
 */
const ActiveJobCard: React.FC<ActiveJobCardProps> = ({
  job,
  onClick,
  completionTitle,
  completionSummary,
  resultNavigation,
  onDismissCompletion,
}) => {
  const displayStatus = getJobDisplayStatus(job);
  const isRunning = displayStatus === 'running';
  const isPending = displayStatus === 'pending';
  const isCompleted = displayStatus === 'completed';
  const isCompletionCard = isCompleted && (!!completionSummary || !!resultNavigation || !!onDismissCompletion);

  // Analyze status updates for parallel progress
  const { latestUpdate, parallelInfo } = useMemo(
    () => analyzeParallelProgress(job._status_updates),
    [job._status_updates]
  );

  // Use animated progress that updates every second based on elapsed time
  const { remainingSeconds } = useAnimatedProgress(job);
  const etaLabel = formatEta(remainingSeconds);

  const handleClick = () => {
    onClick?.(job);
  };

  return (
    <div
      className={`${styles.card} ${isRunning ? styles.running : ''} ${isPending ? styles.pending : ''} ${isCompletionCard ? styles.completed : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={onClick ? 0 : -1}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className={styles.cardContent}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <span className={styles.jobName}>{completionTitle || job.name || 'Unnamed Job'}</span>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.timestamp}>
              {(isRunning || isPending) ? (etaLabel || formatRelativeTime(job._created_at)) : formatRelativeTime(job._completed_at || job._created_at)}
            </span>
          </div>
        </div>

        {isCompletionCard && completionSummary && (
          <div className={styles.completionSummary}>{completionSummary}</div>
        )}

        {/* Show parallel progress summary if multiple branches detected */}
        {!isCompletionCard && parallelInfo && (
          <div className={styles.statusMessage}>
            <span>
              {parallelInfo.stageName} ({formatParallelStatus(parallelInfo)})
            </span>
          </div>
        )}

        {/* Show latest update if no parallel info */}
        {!isCompletionCard && !parallelInfo && latestUpdate && (
          <div className={styles.statusMessage}>
            <span>{latestUpdate.message}</span>
          </div>
        )}

        {!isCompletionCard && !latestUpdate && isPending && (
          <div className={styles.statusMessage}>
            Waiting to start...
          </div>
        )}

        {!isCompletionCard && !latestUpdate && isRunning && (
          <div className={styles.statusMessage}>
            Processing...
          </div>
        )}

        {isCompletionCard && (resultNavigation || onDismissCompletion) && (
          <div className={styles.completionActions}>
            {resultNavigation && (
              <button
                className={styles.resultLink}
                onClick={(event) => {
                  event.stopPropagation();
                  void Promise.resolve(resultNavigation.action());
                }}
              >
                {resultNavigation.label}
              </button>
            )}
            {onDismissCompletion && (
              <button
                className={styles.dismissButton}
                onClick={(event) => {
                  event.stopPropagation();
                  onDismissCompletion(job);
                }}
              >
                Dismiss
              </button>
            )}
          </div>
        )}
      </div>

      <JobProgressBar job={job} variant="board" />
    </div>
  );
};

export default ActiveJobCard;
